import { KnowledgeDocumentListItem } from '@digital-twins/shared-types'

import { CitationImage, httpsImageSrc } from '@/components/citations'

import { citationSocialPost } from '../KnowledgeHubPage/social/SocialCitationPostMeta'
import { EvidenceCitationTarget } from './partitionAssistantCitations'

/** The part of the page's citation context the resolver reads. */
export interface CitationImageContext {
  docsByToken?: Map<string, KnowledgeDocumentListItem>
}

function urlImage(value: unknown): CitationImage | null {
  const src = httpsImageSrc(value)
  return src ? { kind: 'url', src } : null
}

/** A Library document's own cover, else its link thumbnail; never a stock background. */
function libraryImage(documentToken: string | null | undefined, ctx: CitationImageContext): CitationImage | null {
  if (!documentToken) {
    return null
  }
  const doc = ctx.docsByToken?.get(documentToken)
  if (!doc) {
    return null
  }
  return urlImage(doc.coverImageUrl) ?? urlImage(doc.linkThumbnailUrl)
}

/**
 * The one place a citation target becomes a picture (plan C2). Cards, hover
 * cards and source rows all read it, so every surface agrees on what a
 * source shows. Every kind uses data the browser already holds: the web
 * page image and the study cover on the evidence, the Library cover in
 * `docsByToken`, the social post image path on the persisted citation.
 */
export function citationImage(
  target: EvidenceCitationTarget,
  ctx: CitationImageContext,
): CitationImage | null {
  if (target.kind === 'web') {
    return urlImage(target.citation.imageUrl)
  }
  if (target.kind === 'study') {
    return urlImage(target.evidence.imageUrl)
  }
  if (target.kind === 'page_region') {
    return libraryImage(target.evidence.documentToken, ctx)
  }
  if (target.kind === 'connector_record') {
    return null
  }
  if (target.kind === 'document') {
    if (target.sourceKind === 'social') {
      const path = citationSocialPost(target.citation)?.imagePath
      return typeof path === 'string' && path.trim() ? { kind: 'socialPath', path: path.trim() } : null
    }
    return libraryImage(target.citation.documentToken ?? target.evidence.documentToken, ctx)
  }
  return null
}
